import { useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate, Link, Outlet, useLocation } from "react-router-dom";
import { LayoutDashboard, UserPlus, Settings, LogOut } from "lucide-react";
import Navbar from "./Navbar";
import Footer from "./Footer";

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && !user) {
      navigate("/admin/login");
    }
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh", fontFamily: "sans-serif" }}>
        <h3>Loading EliteCare Admin Session...</h3>
      </div>
    );
  }

  if (!user) {
    return null; // Will redirect in useEffect
  }

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const navItems = [
    { label: "Dashboard", path: "/admin-appointments", icon: LayoutDashboard },
    { label: "Admin Users", path: "/admin/users", icon: UserPlus },
    { label: "Settings", path: "/admin/settings", icon: Settings },
  ];

  return (
    <>
      <Navbar />

      <style>{`
        * { box-sizing: border-box; }
        .admin-layout-container {
          min-height: 100vh;
          background: #f8fafc;
          font-family: Arial, sans-serif;
          display: flex;
          flex-direction: column;
        }

        .admin-main {
          display: grid;
          grid-template-columns: 240px 1fr;
          min-height: calc(100vh - 120px);
          max-width: 1400px;
          width: 100%;
          margin: 0 auto;
          padding: 24px 20px;
          gap: 20px;
        }

        .admin-sidebar {
          background: #fff;
          border: 1px solid #e2e8f0;
          border-radius: 20px;
          padding: 20px 12px;
          display: flex;
          flex-direction: column;
          gap: 6px;
          height: fit-content;
          position: sticky;
          top: 80px;
        }

        .sidebar-header {
          padding: 6px 12px 16px;
          border-bottom: 1px solid #e2e8f0;
          margin-bottom: 12px;
        }

        .sidebar-header h4 {
          margin: 0 0 2px;
          font-size: 15px;
          color: #0f172a;
          font-weight: 800;
        }

        .sidebar-header span {
          font-size: 12px;
          color: #16a34a;
          font-weight: 600;
        }

        .sidebar-link {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 14px;
          border-radius: 14px;
          text-decoration: none;
          color: #64748b;
          font-weight: 700;
          font-size: 14px;
          transition: all 0.2s;
          white-space: nowrap;
        }

        .sidebar-link:hover {
          background: #f1f5f9;
          color: #0f172a;
        }

        .sidebar-link.active {
          background: linear-gradient(135deg, #15803d 0%, #16a34a 100%);
          color: #fff;
        }

        .logout-btn {
          margin-top: 16px;
          border: none;
          background: #fef2f2;
          color: #ef4444;
          cursor: pointer;
          font-weight: 800;
        }

        .logout-btn:hover {
          background: #fee2e2;
          color: #b91c1c;
        }

        .admin-content {
          background: #fff;
          border: 1px solid #e2e8f0;
          border-radius: 20px;
          padding: 24px;
          min-height: 500px;
          overflow-x: hidden;
        }

        @media (max-width: 900px) {
          .admin-main {
            grid-template-columns: 1fr;
            padding: 12px;
            gap: 12px;
          }
          .admin-sidebar {
            flex-direction: row;
            flex-wrap: nowrap;
            overflow-x: auto;
            padding: 10px;
            gap: 6px;
            position: static;
            border-radius: 16px;
          }
          .sidebar-header {
            display: none;
          }
          .logout-btn {
            margin-top: 0;
            flex-shrink: 0;
          }
          .sidebar-link {
            flex-shrink: 0;
            padding: 10px 12px;
            font-size: 13px;
          }
          .admin-content {
            padding: 16px;
            border-radius: 16px;
          }
        }

        @media (max-width: 480px) {
          .admin-main {
            padding: 8px;
            gap: 8px;
          }
          .sidebar-link span {
            display: none;
          }
          .sidebar-link {
            padding: 10px;
            border-radius: 12px;
          }
          .admin-content {
            padding: 12px;
          }
        }
      `}</style>

      <div className="admin-layout-container">
        <div className="admin-main">
          <aside className="admin-sidebar">
            <div className="sidebar-header">
              <h4>{user.name}</h4>
              <span>{user.role === "super_admin" ? "Super Admin" : "Administrator"}</span>
            </div>

            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`sidebar-link ${isActive ? "active" : ""}`}
                >
                  <Icon size={18} />
                  <span>{item.label}</span>
                </Link>
              );
            })}

            <button onClick={handleLogout} className="sidebar-link logout-btn">
              <LogOut size={18} />
              <span>Log Out</span>
            </button>
          </aside>

          <main className="admin-content">
            <Outlet />
          </main>
        </div>
      </div>

      <Footer />
    </>
  );
}
